import {
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from "react";

/*
 * MCPBuild shared primitives — the martzmakes design system, ported to the
 * SPA. These render with inline styles against the raw `--token` custom
 * properties (see index.css) so they track the design source pixel-for-pixel.
 */

// ── Logo ──────────────────────────────────────────────────────────────────
// "Snap Block" mark — one brick: socket notch left, plug tab right, square
// port through the centre. Single flat evenodd path, Electric Indigo #635BFF.
export const MB_BRAND = "#635BFF";

export function MBLogoMark({
  size = 18,
  color = MB_BRAND,
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill={color}
      fillRule="evenodd"
      style={{ display: "block", flexShrink: 0 }}
      aria-hidden
    >
      <path d="M2 4H26V12H30V20H26V28H2V20H6V12H2V4ZM12 12H20V20H12V12Z" />
    </svg>
  );
}

// ── Wordmark ──────────────────────────────────────────────────────────────
// Brand-on-dark horizontal lockup: mark + General Sans "MCP" 600 / "Build"
// 400 at −0.036em so the halves read as one word. `size` is the text size;
// the mark scales with it (18px mark against 15px text at nav size).
export function MBWordmark({
  size = 18,
  variant = "default",
}: {
  size?: number;
  variant?: "default" | "classic";
}) {
  const mark = Math.round(size * 1.2);
  const wordmark = (
    <span
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: size,
        letterSpacing: "-0.036em",
        lineHeight: 1,
        color: "var(--fg-1)",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontWeight: 600 }}>MCP</span>
      <span style={{ fontWeight: 400, color: "var(--fg-2)" }}>Build</span>
    </span>
  );
  if (variant === "classic") return wordmark;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: Math.round(mark * 0.36),
      }}
    >
      <MBLogoMark size={mark} />
      {wordmark}
    </span>
  );
}

// ── Status dot ────────────────────────────────────────────────────────────
export function MBDot({
  color = "var(--success)",
  pulse = false,
}: {
  color?: string;
  pulse?: boolean;
}) {
  return (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
        animation: pulse ? "mbPulse 1.6s ease-in-out infinite" : "none",
      }}
    />
  );
}

// ── Chip ──────────────────────────────────────────────────────────────────
export function MBChip({
  children,
  accent = false,
  success = false,
  warn = false,
  style = {},
}: {
  children: ReactNode;
  accent?: boolean;
  success?: boolean;
  warn?: boolean;
  style?: CSSProperties;
}) {
  const color = accent
    ? "var(--accent-fg)"
    : success
      ? "var(--success)"
      : warn
        ? "var(--warn)"
        : "var(--fg-2)";
  const bg = accent
    ? "var(--accent-soft)"
    : success
      ? "var(--success-soft)"
      : warn
        ? "var(--warn-soft)"
        : "var(--surface-2)";
  const border = accent
    ? "var(--accent-line)"
    : success
      ? "rgba(61,207,142,0.32)"
      : warn
        ? "rgba(233,185,73,0.32)"
        : "var(--line-2)";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 9px",
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 4,
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.04em",
        color,
        textTransform: "lowercase",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ── Button ────────────────────────────────────────────────────────────────
type BtnVariant = "primary" | "secondary" | "ghost" | "outline" | "danger";
type BtnSize = "sm" | "md" | "lg";

export function MBBtn({
  children,
  variant = "secondary",
  size = "md",
  icon = null,
  style = {},
  ...rest
}: {
  children: ReactNode;
  variant?: BtnVariant;
  size?: BtnSize;
  icon?: ReactNode;
  style?: CSSProperties;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const h = size === "sm" ? 30 : size === "lg" ? 44 : 36;
  const px = size === "sm" ? 12 : size === "lg" ? 22 : 16;
  const fs = size === "sm" ? 13 : size === "lg" ? 15 : 14;
  const map: Record<BtnVariant, { bg: string; color: string; border: string; hover: string }> = {
    primary: { bg: "var(--accent)", color: "#fff", border: "var(--accent)", hover: "var(--accent-hover)" },
    secondary: { bg: "var(--surface-2)", color: "var(--fg-1)", border: "var(--line-3)", hover: "var(--surface-3)" },
    ghost: { bg: "transparent", color: "var(--fg-2)", border: "transparent", hover: "var(--surface-2)" },
    outline: { bg: "transparent", color: "var(--fg-1)", border: "var(--line-3)", hover: "var(--surface-1)" },
    danger: { bg: "transparent", color: "var(--danger)", border: "var(--danger-line)", hover: "rgba(242,109,109,0.10)" },
  };
  const c = map[variant];
  const [hover, setHover] = useState(false);
  return (
    <button
      {...rest}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        height: h,
        padding: `0 ${px}px`,
        background: hover ? c.hover : c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        borderRadius: 6,
        fontFamily: "var(--font-sans)",
        fontSize: fs,
        fontWeight: 500,
        letterSpacing: "-0.005em",
        cursor: rest.disabled ? "not-allowed" : "pointer",
        opacity: rest.disabled ? 0.5 : 1,
        transition: "all 180ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
      {icon}
    </button>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────
// Lucide-style, 1.5px stroke. Add names here as the pages need them.
const ICON_PATHS: Record<string, string> = {
  arrow: '<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>',
  play: '<polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none"/>',
  check: '<polyline points="20 6 9 17 4 12"/>',
  plus: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
  search: '<circle cx="11" cy="11" r="7.5"/><line x1="21" y1="21" x2="16.5" y2="16.5"/>',
  chat: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  share: '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>',
  layers: '<polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>',
  zap: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
  code: '<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>',
  bot: '<rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/>',
  sparkles: '<path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/><path d="M19 14l.8 2.4L22 17l-2.2.6L19 20l-.8-2.4L16 17l2.2-.6z"/>',
  history: '<path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-6.7 3"/><polyline points="3 4 3 9 8 9"/><polyline points="12 7 12 12 16 14"/>',
  folder: '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
  inbox: '<polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>',
  settings:
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  grid: '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>',
  list: '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>',
  globe: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  lock: '<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  chevron: '<polyline points="9 18 15 12 9 6"/>',
  chevdown: '<polyline points="6 9 12 15 18 9"/>',
  x: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
  copy: '<rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
  eye: '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>',
  star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
  trash:
    '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  download: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>',
  external: '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>',
};

export function MBIcon({
  name,
  size = 16,
  color = "currentColor",
  style = {},
}: {
  name: keyof typeof ICON_PATHS | string;
  size?: number;
  color?: string;
  style?: CSSProperties;
}) {
  const d = ICON_PATHS[name] || "";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      dangerouslySetInnerHTML={{ __html: d }}
    />
  );
}

// ── Eyebrow (mono uppercase section label) ──────────────────────────────────
export function MBEyebrow({
  children,
  color = "var(--accent-fg)",
  style = {},
}: {
  children: ReactNode;
  color?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        letterSpacing: "0.08em",
        color,
        textTransform: "uppercase",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
